import { BehaviorSubject, from, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, first, map, mergeMap, tap } from 'rxjs/operators';
import { Azing as AzingInterface } from './azing-interface/azing.model';
import { Config } from './azing-interface/config.model';
import { AzingManagerService } from './azing-manager.service';
import { DnnUserService } from './dnn-user.service';
import { environment } from './environment';
import { Info } from './info';
import { JwtAuth } from './jwt-interface/model';
import { Lang } from './lang';
import { User } from './user';

declare var jwtAuth: JwtAuth;
declare var azingEnv: any;
declare var window: Window & { azingEnv: any };

export class Azing implements AzingInterface {

  /**
   * azing language
   */
  language: string;

  /**
   * current User data
   */
  user: User;

  /**
   * an Rx stream giving the current User object and which informs about any state changes etc
   */
  current$ = new BehaviorSubject<User>(new User());

  /**
   * an Rx stream giving the current Info object with link to the current users recipe catalog
   */
  info$ = new BehaviorSubject<Info>(new Info());

  private azingConfig = new Config();

  // client for DnnUser WebAPI
  private _dnnUserService: DnnUserService;
  public get dnnUserService(): DnnUserService {
    if (!this._dnnUserService) this._dnnUserService = new DnnUserService(this.azingConfig.host);
    return this._dnnUserService;
  }
  public set dnnUserService(value: DnnUserService) {
    this._dnnUserService = value;
  }

  // client for AzingManager WebAPI
  private _azingManagerService: AzingManagerService;
  public get azingManagerService(): AzingManagerService {
    if (!this._azingManagerService) this._azingManagerService = new AzingManagerService(this.azingConfig.host);
    return this._azingManagerService;
  }
  public set azingManagerService(value: AzingManagerService) {
    this._azingManagerService = value;
  }

  private initDone: Promise<any>;

  constructor() {
    this.initDone = this.init();
    this.initDone
      .then(() => {
        this.current$.pipe(
          distinctUntilChanged(),
          tap((user) => this.user = user),
          filter((user) => user.id !== -1),
          mergeMap(() => from(this.info())),
        ).subscribe();
      });
  }

  private async init() {
    if (!window.azingEnv && document.readyState !== 'complete') {
      await new Promise(resolve => window.addEventListener('load', resolve));
    }

    if (window.azingEnv) {
      const { host, loginUiUrl, registerUiUrl } = azingEnv;
      this.azingConfig = { ...new Config(), host, loginUiUrl, registerUiUrl };
    }

    this.initialize(this.azingConfig);
  }

  private initialize(azingConfig: Partial<Config>) {
    this.language = Lang.getUserLangSetting();
    // set azing host from default config
    this.dnnUserService = new DnnUserService(azingConfig.host);
    this.azingManagerService = new AzingManagerService(azingConfig.host);
    this.registerLogin(azingConfig.loginUiUrl, azingConfig.registerUiUrl);
  }

  config(azingConfig: Partial<Config>) {
    this.azingConfig = { ...new Config(), ...azingConfig };
    this.initialize(this.azingConfig);
  }

  /**
   * called by another JS system which can provide a login UI
   * @param loginUiUrlParam string
   * @param registerUiUrlParam (optional) string
   */
  registerLogin(loginUiUrlParam?: string, registerUiUrlParam?: string) {

    if (loginUiUrlParam) {
      // this.loginUiUrl = loginUiUrl;
      this.azingConfig.loginUiUrl = `${loginUiUrlParam}?${environment.languageKey}=${Lang.getUserLangSetting()}`;
    }
    if (registerUiUrlParam) {
      // this.registerUiUrl = registerUiUrl;
      this.azingConfig.registerUiUrl = `${registerUiUrlParam}?${environment.languageKey}=${Lang.getUserLangSetting()}`;
    } else {
      // this.registerUiUrl = this.loginUiUrl + '#/register';
      this.azingConfig.registerUiUrl = `${this.azingConfig.loginUiUrl + '#/register'}?${environment.languageKey}=${Lang.getUserLangSetting()}`;
    }
    this.dnnUserService.currentUser();
  }

  /**
   * open login UI
   */
  openLogin() {
    this.openLogin$().subscribe({
      next: (next: boolean) => {
        // console.log('isAuthenticated', next);
      },
      error: (error: any) => {
        console.log(error);
      },
      complete: () => {
        // console.log('isAuthenticated done');
      },
    });
  }

  /**
   * open register UI
   */
  openRegister() {
    this.openRegister$().subscribe({
      next: (next: boolean) => {
        // console.log('isAuthenticated', next);
      },
      error: (error: any) => {
        console.log(error);
      },
      complete: () => {
        // console.log('isAuthenticated done');
      },
    });
  }

  /**
   * logout user (without UI)
   */
  executeLogout() {
    this.logout();
  }

  /**
   * calls the other login-system and will return an observable to inform when a login was successful or cancelled
   */
  openLogin$(): Observable<boolean> {
    const isAuthenticated$ = new Subject<boolean>();

    const callback = () => {
      isAuthenticated$.next(this.user.isAuthenticated);
      isAuthenticated$.complete();
    };
    (window as any).$2sxc.totalPopup.open(this.azingConfig.loginUiUrl, callback);

    // this.postDataToPopup();

    return isAuthenticated$;
  }

  // // solution for "Uncaught DOMException: Blocked a frame with origin from accessing a cross-origin frame."
  // private postDataToPopup() {
  //   // get first frame, because we do not have frame-id
  //   const data = {
  //     azing: JSON.parse(JSON.stringify((window as any).azing)),
  //     azingEnv
  //   };
  //   var frame = document.getElementsByTagName('iframe')[0];
  //   console.log('stv start', frame, data);
  //   frame.contentWindow.postMessage(data, '*');

  //   console.log('stv end', data);
  // }

  /**
   * calls the other login-system and will return an observable to inform when a register (and login) was successful or cancelled
   */
  openRegister$(): Observable<boolean> {
    const isAuthenticated$ = new Subject<boolean>();
    const callback = () => {
      isAuthenticated$.next(this.user.isAuthenticated);
      isAuthenticated$.complete();
    };
    (window as any).$2sxc.totalPopup.open(this.azingConfig.registerUiUrl, callback);

    return isAuthenticated$;
  }

  /**
   * function to close login from other system
   */
  closeLogin() {
    const $2sxc = (window as any).$2sxc;
    if ($2sxc) {
      $2sxc.totalPopup.close();
    } else {
      window.postMessage('close', '*');
    }
  }

  /**
   * get user info from WebAPI
   */
  currentUser$ = (): Observable<User> => from(this.currentUser()); // .pipe(share());

  private currentUser = (): Promise<User> => {
    return this.dnnUserService.currentUser();
  }

  /**
   * lets a user login with WebAPI, resulting in a DNN login
   */
  login$ = (username: string, password: string): Observable<User> => from(this.login(username, password));

  private login = (username: string, password: string): Promise<User> => {
    return this.dnnUserService.login(username, password).then((rez) => {
      // HACK: ensure jwt token in session storage
      if ((window as any).jwtAuth) {
        jwtAuth = new (jwtAuth as any).constructor((jwtAuth as any).jwtBase, (jwtAuth as any).whiteList);
      }
      return rez;
    }).then((rez) => {
      return this.dnnUserService.currentUser();
    });
  }

  /**
   * user logout with WebAPI, resulting in a DNN logout
   */
  logout$ = (): Observable<User> => from(this.logout());

  private logout = (): Promise<User> => {
    return this.dnnUserService.logout().then((rez) => {
      sessionStorage.removeItem(this.azingConfig.sessionStorageKey);
      return this.dnnUserService.currentUser();
    });
  }

  /**
   * sign-up/register new user with WebAPI, resulting in creation of new DNN user and DNN login
   */
  register$ = (email: string, password: string): Observable<boolean> => from(this.register(email, password));

  private register = (email: string, password: string): Promise<boolean> => {
    return this.dnnUserService.register(email, password).then((rez) => {
      // HACK: ensure jwt token in session storage
      if ((window as any).jwtAuth) {
        jwtAuth = new (jwtAuth as any).constructor((jwtAuth as any).jwtBase, (jwtAuth as any).whiteList);
      }
      return rez;
    });
  }

  /**
   * for forgot password, call send link reset option with WebAPI
   */
  forgot$ = (username: string): Observable<boolean> => from(this.forgot(username));

  private forgot = (username: string): Promise<boolean> => {
    return this.dnnUserService.forgot(username);
  }

  /**
   * get link to the current users recipe catalog
   */
  myRecipesChecklist() {
    this.info().then((info) => {
      if (info && info.url) {
        window.location.href = `/${info.url}`;
      }
    });
  }

  /**
   * get current Info object with WebAPI, resulting in link to the current users recipe catalog
   */
  private info = (): Promise<Info> => {
    return this.initDone
      .then(() => this.azingManagerService.info());
  }


  /**
   * get DisplayName from User data
   * @param user
   */
  getDisplayName(user: User): string {
    if (!user || !user.id || user.id === -1) return '';

    if (!!user.displayname) { return user.displayname.trim(); }

    if (!!user.firstname || !!user.lastname) {
      const firstName = !!user.firstname ? user.firstname.trim() : '';
      const lastName = !!user.lastname ? user.lastname.trim() : '';
      const firstAndLastName = `${firstName} ${lastName}`;
      return firstAndLastName.trim();
    }

    return user.username.trim();
  }
}
